import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { faHotel, faNewspaper, faPlane, faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { createContext, useContext } from 'react';
import { useIntl } from 'react-intl';
import { BookerContext } from '../Booker/index.js';
import { BedIcon } from '../Booker/components/HotelSelector.js';
import { cn, cnWithPrefix } from 'ui/lib/utils';
const NavBarContext = createContext({});
const PlaneIcon = ({ className }) => {
    const { useTailwindPrefix } = useContext(NavBarContext);
    const customCn = useTailwindPrefix ? cnWithPrefix('tw-') : cn;
    return (_jsx("svg", Object.assign({ className: customCn({
            'h-[1em] overflow-visible box-content': !useTailwindPrefix,
            'tw-h-[1em] tw-overflow-visible tw-box-content': useTailwindPrefix,
        }, className), xmlns: "http://www.w3.org/2000/svg", viewBox: "0 -960 960 960", fill: "currentColor" }, { children: _jsx("path", { d: "M120-120v-80h720v80H120Zm70-200L40-570l96-26 112 94 140-37-207-276 116-31 299 251 170-46q32-9 60.5 7.5T864-585q9 32-7.5 60.5T808-487L190-320Z" }) })));
};
const Item = ({ children, value, className, url, icon, innerClassName }) => {
    const { onChange, selected, selectedClassName, useTailwindPrefix } = useContext(NavBarContext);
    const { version } = useContext(BookerContext);
    const onClick = () => {
        onChange && onChange(value);
    };
    const customCn = useTailwindPrefix ? cnWithPrefix('tw-') : cn;
    const itemClassName = customCn('itm-booker-navItem', {
        'py-2 px-4 md:px-8 rounded-full block whitespace-nowrap truncate w-full': version === 'padded' && !useTailwindPrefix,
        'tw-py-2 tw-px-4 md:tw-px-8 tw-rounded-full tw-block tw-whitespace-nowrap tw-truncate tw-w-full': version === 'padded' && useTailwindPrefix,
        'bg-primary-booker text-primary-booker-a11y': version === 'padded' && selected === value && !useTailwindPrefix,
        'tw-bg-primary-booker tw-text-primary-booker-a11y': version === 'padded' && selected === value && useTailwindPrefix,
        'bg-widget-pill-background backdrop-blur text-widget-pill-a11y cursor-pointer': version === 'padded' && selected !== value && !useTailwindPrefix,
        'tw-bg-widget-pill-background tw-backdrop-blur tw-text-widget-pill-a11y tw-cursor-pointer': version === 'padded' && selected !== value && useTailwindPrefix,
        'relative block py-2 px-6 border-b-[3px] whitespace-nowrap truncate': version !== 'padded' && !useTailwindPrefix,
        'tw-relative tw-block tw-py-2 tw-px-6 tw-border-b-[3px] tw-whitespace-nowrap tw-truncate': version !== 'padded' && useTailwindPrefix,
        'border-gray-300 cursor-default': selected === value && !useTailwindPrefix,
        'tw-border-gray-300 tw-cursor-default': selected === value && useTailwindPrefix,
        'border-transparent cursor-pointer': selected !== value && !useTailwindPrefix,
        'tw-border-transparent tw-cursor-pointer': selected !== value && useTailwindPrefix,
    }, innerClassName);
    const itemContent = (_jsxs(_Fragment, { children: [icon &&
                (icon.indexOf('plane') >= 0 ? (_jsxs(_Fragment, { children: [_jsx(FontAwesomeIcon, { icon: faPlane, rotation: 270 }), "\u00A0"] })) : icon.indexOf('newspaper') >= 0 ? (_jsxs(_Fragment, { children: [_jsx(FontAwesomeIcon, { icon: faNewspaper }), "\u00A0"] })) : null), children] }));
    return (_jsx("li", Object.assign({ className: classNames(className, selectedClassName && selected === value && `${selectedClassName}`, {
            'relative block': !useTailwindPrefix,
            'tw-relative tw-block': useTailwindPrefix,
        }) }, { children: url ? (_jsx("a", Object.assign({ className: itemClassName, href: url, target: "_blank", rel: "nofollow" }, { children: itemContent }))) : (_jsx("button", Object.assign({ className: itemClassName, onClick: onClick }, { children: itemContent }))) })));
};
const HotelItem = ({ isClear }) => {
    const intl = useIntl();
    const { version, useTailwindPrefix } = useContext(BookerContext);
    return (_jsxs(Item, Object.assign({ value: "hotel", innerClassName: classNames({
            'bg-widget-pill-background text-transparent': version === 'padded' && isClear && !useTailwindPrefix,
            'tw-bg-widget-pill-background tw-text-transparent': version === 'padded' && isClear && useTailwindPrefix,
        }) }, { children: [version === 'padded' ? (_jsx(BedIcon, { className: classNames({
                    'inline-block align-[-0.125rem]': !useTailwindPrefix,
                    'tw-inline-block tw-align-[-0.125rem]': useTailwindPrefix,
                }) })) : (_jsx(FontAwesomeIcon, { icon: faHotel })), "\u00A0", intl.formatMessage({ id: 'booker.hotel' })] })));
};
const HotelFlightItem = () => {
    const intl = useIntl();
    const { version, useTailwindPrefix } = useContext(BookerContext);
    return (_jsxs(Item, Object.assign({ value: "hotelFlight" }, { children: [version === 'padded' ? (_jsx(PlaneIcon, { className: classNames({
                    'inline-block align-[-0.125rem]': !useTailwindPrefix,
                    'tw-inline-block tw-align-[-0.125rem]': useTailwindPrefix,
                }) })) : (_jsx(FontAwesomeIcon, { icon: faPlane, rotation: 270 })), "\u00A0", intl.formatMessage({ id: 'booker.hotel' }), "\u00A0+\u00A0", intl.formatMessage({ id: 'booker.flight' })] })));
};
const ServiceItem = () => {
    const intl = useIntl();
    return (_jsxs(Item, Object.assign({ value: "service" }, { children: [_jsx(FontAwesomeIcon, { icon: faStar }), "\u00A0", intl.formatMessage({ id: 'booker.services' })] })));
};
export const NavBar = ({ children, onChange, selected, className, selectedClassName, useTailwindPrefix }) => {
    const { version } = useContext(BookerContext);
    return (_jsx(NavBarContext.Provider, Object.assign({ value: { onChange, selected, selectedClassName, useTailwindPrefix } }, { children: _jsx("ul", Object.assign({ className: classNames(className, {
                'space-x-4': version === 'padded' && !useTailwindPrefix,
                'tw-space-x-4': version === 'padded' && useTailwindPrefix,
                'mb-3.5 bg-transparent flex': !useTailwindPrefix,
                'tw-mb-3.5 tw-bg-transparent tw-flex': useTailwindPrefix,
            }) }, { children: children })) })));
};
NavBar.Item = Item;
NavBar.HotelItem = HotelItem;
NavBar.HotelFlightItem = HotelFlightItem;
NavBar.ServiceItem = ServiceItem;
